import React, { useMemo } from 'react';

import classnames from 'classnames';
import isArray from 'lodash/isArray';

import Image, { TRANSFORMATIONS } from 'components/uiLibrary/Image';
import Typography from 'components/uiLibrary/Typography';
import VideoPlayer from 'components/uiLibrary/VideoPlayer';

import GallerySpaceFiller from 'components/Globals/Media/GallerySpaceFiller';
// eslint-disable-next-line import/no-cycle
import { usePaywallContext } from 'components/Paywall';

import { MEDIA_TYPES_IDS, DATE_FORMATS } from 'constants/index';

import { createDate, getFormattedDateRange } from 'utils/date';
import { getMediaSource } from 'utils/media';
import { getProductionTitles } from 'utils/productions';

import classes from './FeatureMedia.module.scss';

const FeatureMedia = ({ data, producer, trackingData, styles }) => {
  const { setIsPaywallExpanded } = usePaywallContext();
  const titles = useMemo(() => getProductionTitles(data), [data]);
  const { media, minDate, maxDate } = data || {};
  const isArchived = data?.isArchived;
  const mediaItem = useMemo(() => {
    let filteredMediaList = isArray(media) ? media : [media];
    filteredMediaList = filteredMediaList?.filter(item => {
      if (item?.mediaType?.id === MEDIA_TYPES_IDS.VIDEO) {
        return true;
      }

      return item?.file && Object.keys(item?.file?.urls)?.length > 0;
    });

    const videoItem = filteredMediaList?.find(item => item?.mediaType?.id === MEDIA_TYPES_IDS.VIDEO);

    if (videoItem) {
      return videoItem;
    }

    return filteredMediaList?.[0];
  }, [media]);

  const genre = data?.productionWorks?.[0]?.productionWorkToWorkTypes?.[0]?.workType?.name;

  const { fromISO, toISO } = useMemo(
    () =>
      getFormattedDateRange({
        from: minDate,
        to: maxDate || minDate,
        asParts: true,
        dateFormat: DATE_FORMATS.DAY_MONTH,
      }),
    [maxDate, minDate],
  );

  const startYear = createDate(fromISO).format(DATE_FORMATS.YEAR);
  const endYear = toISO ? createDate(toISO).format(DATE_FORMATS.YEAR) : null;

  if (!media) {
    return (
      <div className={classnames(classes.featureMedia, { [styles.featureMedia]: !!styles.featureMedia })}>
        <div className={classes.featureMediaGallerySpaceFiller}>
          <GallerySpaceFiller text={titles?.[0]?.title} transformations={TRANSFORMATIONS.SLUG_MEDIA} />
        </div>

        {producer?.id && (
          <div className={classes.featureMedia__logo}>
            <Image
              src={producer?.image?.medium}
              width={30}
              height={30}
              transformations={TRANSFORMATIONS.PROFILE_THUMBNAIL}
              blurHash={producer?.image?.blurHash}
              alt={producer?.name}
              disableNextImage
            />
          </div>
        )}

        <div className={classes.featureMedia__seasonYear}>
          <Typography className={classes.date} size={12} color="white" weight="bold">
            <time dateTime={fromISO}>{startYear}</time>
            {endYear !== startYear && (
              <>
                {endYear && '-'}
                {endYear && <time dateTime={fromISO}>{endYear}</time>}
              </>
            )}
          </Typography>
          {genre && (
            <Typography size={10} weight="medium" blur={isArchived}>
              {genre}
            </Typography>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={classnames(classes.featureMedia, { [styles.featureMedia]: !!styles.featureMedia })}>
      {mediaItem?.mediaType?.id === MEDIA_TYPES_IDS.VIDEO ? (
        <VideoPlayer media={mediaItem} width={345} height={194} inline />
      ) : (
        <div className={classes.imageBg}>
          <Image
            src={getMediaSource(mediaItem)}
            transformations={{
              ...TRANSFORMATIONS.PRODUCTION_MEDIA_SLUG,
              borderRadius: 8,
            }}
            blurHash={mediaItem?.blurHash}
            alt={mediaItem?.title}
            title={mediaItem?.title}
            attribution={{
              source_info: mediaItem?.source_info,
              credit: mediaItem?.credit,
              copyright: mediaItem?.copyright,
            }}
            disableNextImage
            useIntersectionObserver
            trackingData={trackingData}
            lazy
            width={302}
            height={192}
          />
        </div>
      )}

      {producer?.id && (
        <div className={classes.featureMedia__logo}>
          <Image
            src={producer?.image?.medium}
            width={30}
            height={30}
            transformations={TRANSFORMATIONS.PROFILE_THUMBNAIL}
            blurHash={producer?.image?.blurHash}
            alt={producer?.name}
            disableNextImage
          />
        </div>
      )}

      <div className={classes.featureMedia__seasonYear}>
        <Typography
          className={classes.date}
          size={11}
          color="white"
          weight="bold"
          {...(isArchived && {
            onClick: e => {
              e.stopPropagation();
              e.preventDefault();
              setIsPaywallExpanded(true);
            },
          })}
        >
          <time dateTime={fromISO}>{startYear}</time>
          {endYear !== startYear && (
            <>
              {endYear && '-'}
              {endYear && <time dateTime={fromISO}>{endYear}</time>}
            </>
          )}
        </Typography>
        {genre && (
          <Typography size={10} weight="medium" blur={isArchived}>
            {genre}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default FeatureMedia;
